<script lang="ts">
    // Svelte/Jet/Models/Types
    import { getI18n } from '~/stores/i18n';
    import {
        type ContainerDetailHeader as ContainerDetailHeaderType,
        ContentKind,
    } from '~/jet/models';

    // Icon Assets
    import ShareIcon from '~assets/icons/share.svg';

    // Utilities
    import { getCommerce } from '~/utils/commerce';
    import { isPremiumSubscriber } from '~/utils/user';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import MotionVideo from '~/components/jet/Artwork/MotionVideo.svelte';
    import ButtonAction from '~/components/jet/action/ButtonAction.svelte';
    import ClickAction from '~/components/jet/action/ClickAction.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import CloudButtons from '~/components/jet/buttons/CloudButtons.svelte';

    // Shared Components
    import ContainerDetailHeader from '@amp/web-app-components/src/components/ContainerDetailHeader/ContainerDetailHeader.svelte';
    import SeparatedList from '@amp/web-app-components/src/components/helpers/SeparatedList.svelte';
    import { shareLinkOrCopy } from '~/utils/share';
    import { getJet } from '~/jet';

    // PageHeaderType contains values that may not relate to a content kind
    // ex: "title" and "tabs".  Only need the kinds that would require a
    // type for fallback artwork.
    const HEADER_TYPE_TO_KIND = {
        artist: ContentKind.Artist,
        playlist: ContentKind.Playlist,
    };

    const i18n = getI18n();
    const { state: commerceState } = getCommerce();
    const jet = getJet();

    export let header: ContainerDetailHeaderType;

    $: artwork = header.artwork;
    $: videoArtwork = header.videoArtwork;
    $: title = header.title;
    $: subtitle = header.subtitle;
    $: subtitleLinks = header.subtitleLinks ?? [];
    $: metadataTop = header.metadataTop;
    $: year = header.year;
    $: lastUpdated = header.lastUpdated;

    $: modal = header.modalPresentationDescriptor;
    $: modalText = modal?.paragraphText;
    $: modalTitle = modal?.headerTitle;
    $: modalSubtitle = modal?.headerSubtitle;

    $: isPremiumUser = isPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );

    $: isUserInHomeStorefront = jet.isUserInHomeStorefront();

    $: isRecording = header.type.includes('recording');

    // TODO: See if width/height ever comes down from API
    // https://github.pie.apple.com/amp-web/desktop-music-app/blob/57b97186a4d5c35484d557ddfaa814733a417b76/app/components/dt-product-lockup.js#L65-L89
    $: profile = (() => {
        return artwork?.width !== artwork?.height
            ? 'product-rectangle'
            : 'product';
    })();
</script>

<div class="container-detail-header-container" class:is-recording={isRecording}>
    <ContainerDetailHeader
        {title}
        {modalTitle}
        {modalSubtitle}
        {modalText}
        useExpandedHeadings={true}
        translateFn={$i18n.t}
    >
        <svelte:fragment slot="metadata-top">
            {#if metadataTop}
                {metadataTop}
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="subtitles">
            {#if subtitleLinks.length}
                <SeparatedList
                    items={subtitleLinks}
                    translateFn={$i18n.t}
                    let:item={subtitle}
                >
                    <ClickAction action={subtitle.segue}
                        >{subtitle.title}</ClickAction
                    >
                </SeparatedList>
            {:else if subtitle}
                {subtitle}
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="metadata-bottom">
            {#if lastUpdated}
                <span data-testid="container-detail-header-last-updated"
                    >{lastUpdated}</span
                >
            {/if}

            {#if year}
                <span data-testid="container-detail-header-year">{year}</span>
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="play-button">
            {#if header.primaryButton}
                <!-- title={isPremiumUser ? header.primaryButton?.title : $i18n.t('ASE.Web.MusicClassical.Preview')} -->
                <ButtonAction
                    action={header.primaryButton?.action}
                    title={isPremiumUser
                        ? header.primaryButton?.title
                        : $i18n.t('ASE.Web.MusicClassical.Preview')}
                    styleOverride={{ isPlay: true, isPrimary: true }}
                />
            {/if}
        </svelte:fragment>

        <!-- TODO: rdar://137428544 (Web Classical Music: Album/Playlist Detail Pages - Drag and Drop Support) -->
        <div
            slot="artwork"
            class="artwork"
            class:artwork-reduced-size={isRecording}
        >
            <div class="artwork__radiosity">
                <!-- use static image for radiosity effect -->
                {#if artwork}
                    <Artwork
                        {artwork}
                        {profile}
                        useContainerStyle={false}
                        forceFullWidth={false}
                        alt=""
                    />
                {/if}
            </div>

            <MotionVideo
                {artwork}
                {videoArtwork}
                {profile}
                useContainerStyle={false}
                fallbackKind={HEADER_TYPE_TO_KIND[header.type]}
                forceCropCode={true}
                itemKind="containerDetailHeaderLockup"
                alt={title}
            />
        </div>

        <svelte:fragment slot="secondary-actions">
            {#if header?.shareButton && !header?.contextMenuAction}
                <button
                    data-testid="share-button"
                    aria-label={header.shareButton.title}
                    class="share-button"
                    on:click={() =>
                        shareLinkOrCopy({
                            url: header.shareButton?.action?.url,
                        })}
                >
                    <ShareIcon />
                </button>
            {/if}
            {#if isPremiumUser && isUserInHomeStorefront}
                <CloudButtons id={header?.id} type={header?.type} />
            {/if}
            {#if header?.contextMenuAction}
                <ProductContextMenu
                    contextMenuAction={header?.contextMenuAction}
                    hasMaterial={false}
                />
            {/if}
        </svelte:fragment>
    </ContainerDetailHeader>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/mixins/radiosity-effect' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .artwork__radiosity {
        --artwork-override-max-height: 100%;

        @include radiosity-effect;

        // For Safari, Prevent artwork shadow from flickering
        @include target-safari {
            transform: scale(0.88) translateZ(0);
        }
    }

    .container-detail-header-container {
        // Corresponds to margin above primary action buttons (e.g. Play)
        // Used for balancing/centering all metadata in space above play button.
        $primary-actions-top-spacer: 18px;
        display: contents;

        @media (--small) {
            --containerDetailHeaderSpacer: 34px;

            &.is-recording {
                --containerDetailHeaderSpacer: 33px;
            }
        }

        .artwork-reduced-size {
            --artworkSize: calc(270px * 0.78); // 78% of Album/Playlist artwork
        }

        :global(.container-detail-header) {
            @media (--small) {
                // Add extra "free space" rows to grid to balance headings and description in center
                grid-template-areas:
                    'artwork    .                   .' // Fixed-size row balanced with spacer above primary actions
                    'artwork    .                   .' // 1fr - balance space above metadata
                    'artwork    headings            headings'
                    'artwork    description         description'
                    'artwork    .                   .' // 1fr - balance space below metadata
                    'artwork    primary-actions     secondary-actions';
                grid-template-rows: $primary-actions-top-spacer 1fr auto auto 1fr auto;
            }
        }

        :global(.headings__metadata-top) {
            font: var(--callout-emphasized);
            text-transform: uppercase;
            margin-top: 0;
            margin-bottom: 1px;
        }

        :global(.headings__title) {
            font: var(--large-title-emphasized-serif);
        }

        :global(.headings__title--fluid-size) {
            font: var(--title-1-emphasized-serif);
        }

        :global(.headings__metadata-bottom) {
            text-transform: capitalize;
        }

        :global(.container-detail-header--metadata-tall .artwork) {
            align-self: start;
        }

        :global(.headings + .primary-actions) {
            margin-top: $primary-actions-top-spacer;
        }

        :global(.secondary-actions) {
            // Contextual menu button colors
            --contextMenuCircleFillOverride: var(--systemQuinary);
            --contextMenuEllipsisFillOverride: var(--keyColor);
            --contextMenuCircleHoverFill: var(--systemQuinary);
            --contextMenuEllipsisHoverFill: var(--keyColor);

            @media (--small) {
                padding-inline-start: 8px;
                margin-inline-end: 18px;
            }
        }

        :global(.add-to-library-button) {
            margin-inline-end: 0;
        }
    }

    .share-button {
        background: var(--shareButtonBackground);
        border-radius: inherit;
        width: 100%;
        height: 100%;
        color: var(--keyColor);

        :global(svg) {
            width: 13px;
            transform: translateY(-1px);
        }
    }
</style>
